import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import LanguageSwitcher from './LanguageSwitcher';
import {
  Globe,
  Loader2,
  CheckCircle2,
  XCircle,
  ArrowRight,
  Sparkles,
  MessageSquare,
  Bot,
  FileText,
  Palette,
  Plus,
  Trash2,
  Check,
  Wand2,
} from 'lucide-react';

interface OnboardingWizardProps {
  onComplete: () => void;
  onSkip: () => void;
}

type Step = 'welcome' | 'crawl' | 'crawling' | 'test' | 'theme' | 'domains' | 'deploy';

interface ThemeConfig {
  appearance: {
    position: string;
    launcher: { shape: string; size: string };
    colors: {
      primary: string;
      bg: string;
      text: string;
      border: string;
      muted: string;
    };
    border: { width: number; radius: number; shadow: number };
    typography: { family: string; scale: string };
    icon: { type: string };
    animation: { type: string; durationMs: number; respectReduceMotion: boolean };
  };
  persona: {
    greeting: string;
    tone: string;
    emoji: boolean;
    handoffLabel: string;
    avatar: { image: string; bgColor: string };
  };
  behavior: {
    trigger: { type: string };
    handoffConfidence: number;
    locale: string;
  };
  accessibility: {
    focusRing: boolean;
    minContrast: string;
  };
}

interface Domain {
  id: string;
  host: string;
  is_active: boolean;
  created_at: string;
}

const DEFAULT_THEME_CONFIG: ThemeConfig = {
  appearance: {
    position: 'bottom-right',
    launcher: { shape: 'capsule', size: 'm' },
    colors: {
      primary: '#FF6A00',
      bg: '#FFFFFF',
      text: '#0A0A0A',
      border: '#000000',
      muted: '#666666',
    },
    border: { width: 2, radius: 24, shadow: 2 },
    typography: { family: 'Inter', scale: 'base' },
    icon: { type: 'chat-bubble' },
    animation: { type: 'pulse', durationMs: 300, respectReduceMotion: true },
  },
  persona: {
    greeting: 'How can I help you today?',
    tone: 'helpful',
    emoji: false,
    handoffLabel: 'Talk to a person',
    avatar: {
      image: 'https://media.tahc.ai/profile-pictures/hybrid-chat-done-right-blue-face.webp',
      bgColor: '#FF6A00',
    },
  },
  behavior: {
    trigger: { type: 'manual' },
    handoffConfidence: 0.45,
    locale: 'en-US',
  },
  accessibility: {
    focusRing: true,
    minContrast: 'AA',
  },
};

export default function OnboardingWizard({ onComplete, onSkip }: OnboardingWizardProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('welcome');
  const [url, setUrl] = useState('');
  const [crawlJobId, setCrawlJobId] = useState<string | null>(null);
  const [crawlStatus, setCrawlStatus] = useState<{
    status: string;
    pages_discovered: number;
    pages_crawled: number;
    pages_indexed: number;
    error_message?: string;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Array<{
    question: string;
    answer: string;
    confidence: number;
  }> | null>(null);
  const [isTestingAI, setIsTestingAI] = useState(false);

  // Theme state
  const [themeConfig, setThemeConfig] = useState<ThemeConfig>(DEFAULT_THEME_CONFIG);
  const [themeName, setThemeName] = useState('');
  const [isExtractingBrand, setIsExtractingBrand] = useState(false);

  // Domain state
  const [domains, setDomains] = useState<Domain[]>([]);
  const [newDomain, setNewDomain] = useState('');
  const [isAddingDomain, setIsAddingDomain] = useState(false);

  // Poll crawl status
  useEffect(() => {
    if (!crawlJobId || step !== 'crawling') return;

    const pollInterval = setInterval(async () => {
      try {
        const status = await api.getCrawlStatus(crawlJobId);
        setCrawlStatus(status);

        if (status.status === 'completed') {
          clearInterval(pollInterval);
          // Small delay before moving to test step
          setTimeout(() => setStep('test'), 1000);
        } else if (status.status === 'failed') {
          clearInterval(pollInterval);
          setError(status.error_message || t('onboarding.crawlFailed'));
        }
      } catch (err) {
        console.error('Failed to get crawl status:', err);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [crawlJobId, step, t]);

  // Extract brand colors when entering theme step
  useEffect(() => {
    if (step === 'theme' && url && !isExtractingBrand && themeConfig.appearance.colors.primary === DEFAULT_THEME_CONFIG.appearance.colors.primary) {
      extractBrandColors();
    }
  }, [step]);

  // Load existing domains when entering domains step
  useEffect(() => {
    if (step === 'domains') {
      loadDomains();
    }
  }, [step]);

  const extractBrandColors = async () => {
    if (!url.trim()) return;

    setIsExtractingBrand(true);
    setError(null);

    try {
      const result = await api.extractBrand(url);

      // Update theme config with extracted colors
      setThemeConfig(prev => ({
        ...prev,
        appearance: {
          ...prev.appearance,
          colors: {
            ...prev.appearance.colors,
            primary: result.colors.primary || prev.appearance.colors.primary,
            border: result.colors.secondary || prev.appearance.colors.border,
          },
        },
      }));

      // Set theme name based on domain
      const domain = result.extractedFrom.replace('www.', '');
      setThemeName(`${domain} Theme`);
    } catch (err: any) {
      console.error('Failed to extract brand:', err);
      // Use default theme if extraction fails - not critical
    } finally {
      setIsExtractingBrand(false);
    }
  };

  const loadDomains = async () => {
    try {
      const domainsData = await api.getDomains();
      setDomains(domainsData);
    } catch (err) {
      console.error('Failed to load domains:', err);
    }
  };

  const handleStartCrawl = async () => {
    if (!url.trim()) {
      setError(t('onboarding.urlRequired'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await api.startFullSiteCrawl(url);
      setCrawlJobId(result.job_id);
      setStep('crawling');
    } catch (err: any) {
      setError(err.response?.data?.error || t('onboarding.crawlError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTestAI = async () => {
    setIsTestingAI(true);
    setError(null);

    try {
      const result = await api.validateAI();
      setTestResults(result.results);
    } catch (err: any) {
      setError(err.response?.data?.error || t('onboarding.testError'));
    } finally {
      setIsTestingAI(false);
    }
  };

  const handleCreateTheme = async () => {
    if (!themeName.trim()) {
      setError(t('onboarding.themeNameRequired'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const theme = await api.createTheme(themeName, themeConfig);

      // Activate the theme
      await api.activateTheme(theme.id);

      // Move to domains step
      setStep('domains');
    } catch (err: any) {
      setError(err.response?.data?.error || t('onboarding.createThemeError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddDomain = async () => {
    if (!newDomain.trim()) return;

    setIsAddingDomain(true);
    setError(null);

    try {
      const domain = await api.addDomain(newDomain.trim());
      setDomains(prev => [...prev, domain]);
      setNewDomain('');
    } catch (err: any) {
      setError(err.response?.data?.error || t('onboarding.addDomainError'));
    } finally {
      setIsAddingDomain(false);
    }
  };

  const handleRemoveDomain = async (domainId: string) => {
    try {
      await api.removeDomain(domainId);
      setDomains(prev => prev.filter(d => d.id !== domainId));
    } catch (err: any) {
      setError(err.response?.data?.error || t('onboarding.removeDomainError'));
    }
  };

  const handleActivateAndFinish = async () => {
    setIsSubmitting(true);
    try {
      await api.activateAI();
      onComplete();
    } catch (err: any) {
      setError(err.response?.data?.error || t('onboarding.activateError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderWelcome = () => (
    <div className="text-center max-w-2xl mx-auto">
      {/* Language Switcher */}
      <div className="flex justify-end mb-4">
        <LanguageSwitcher />
      </div>

      <img
        src="/tahc-logo-2026.jpg"
        alt="tahc"
        className="w-20 h-20 mx-auto mb-6 object-contain"
      />
      <h1 className="text-3xl font-bold text-gray-900 mb-4">
        {t('onboarding.welcomeTitle')}
      </h1>
      <p className="text-lg text-gray-600 mb-8">
        {t('onboarding.welcomeSubtitle')}
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <div className="bg-white p-5 rounded-xl border border-gray-200">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-3 mx-auto">
            <Globe className="w-5 h-5 text-blue-600" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-1 text-sm">{t('onboarding.step1Title')}</h3>
          <p className="text-xs text-gray-500">{t('onboarding.step1Desc')}</p>
        </div>

        <div className="bg-white p-5 rounded-xl border border-gray-200">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-3 mx-auto">
            <Bot className="w-5 h-5 text-green-600" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-1 text-sm">{t('onboarding.step2Title')}</h3>
          <p className="text-xs text-gray-500">{t('onboarding.step2Desc')}</p>
        </div>

        <div className="bg-white p-5 rounded-xl border border-gray-200">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-3 mx-auto">
            <Palette className="w-5 h-5 text-purple-600" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-1 text-sm">{t('onboarding.step3Title')}</h3>
          <p className="text-xs text-gray-500">{t('onboarding.step3Desc')}</p>
        </div>

        <div className="bg-white p-5 rounded-xl border border-gray-200">
          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mb-3 mx-auto">
            <MessageSquare className="w-5 h-5 text-orange-600" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-1 text-sm">{t('onboarding.step4Title')}</h3>
          <p className="text-xs text-gray-500">{t('onboarding.step4Desc')}</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <button
          onClick={() => setStep('crawl')}
          className="px-8 py-3 bg-black text-white rounded-full font-medium hover:bg-gray-800 transition flex items-center justify-center gap-2"
        >
          {t('onboarding.getStarted')}
          <ArrowRight className="w-5 h-5" />
        </button>
        <button
          onClick={onSkip}
          className="px-8 py-3 text-gray-600 hover:text-gray-900 transition"
        >
          {t('onboarding.skipForNow')}
        </button>
      </div>
    </div>
  );

  const renderCrawl = () => (
    <div className="max-w-xl mx-auto">
      <button
        onClick={() => setStep('welcome')}
        className="text-gray-500 hover:text-gray-700 mb-6 flex items-center gap-1"
      >
        &larr; {t('common.back')}
      </button>

      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Globe className="w-8 h-8 text-blue-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {t('onboarding.crawlTitle')}
        </h2>
        <p className="text-gray-600">
          {t('onboarding.crawlSubtitle')}
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('onboarding.websiteUrl')}
        </label>
        <input
          type="text"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://example.com"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
          onKeyDown={(e) => e.key === 'Enter' && handleStartCrawl()}
        />
        <p className="text-sm text-gray-500 mt-2">
          {t('onboarding.crawlHint')}
        </p>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        <button
          onClick={handleStartCrawl}
          disabled={isSubmitting || !url.trim()}
          className="w-full mt-6 px-6 py-3 bg-black text-white rounded-full font-medium hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              {t('onboarding.starting')}
            </>
          ) : (
            <>
              {t('onboarding.startCrawl')}
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>
      </div>
    </div>
  );

  const renderCrawling = () => (
    <div className="max-w-xl mx-auto text-center">
      <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">
        {t('onboarding.crawlingTitle')}
      </h2>
      <p className="text-gray-600 mb-8">
        {t('onboarding.crawlingSubtitle')}
      </p>

      {crawlStatus && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div>
              <div className="text-3xl font-bold text-gray-900">
                {crawlStatus.pages_discovered}
              </div>
              <div className="text-sm text-gray-500">{t('onboarding.discovered')}</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-gray-900">
                {crawlStatus.pages_crawled}
              </div>
              <div className="text-sm text-gray-500">{t('onboarding.crawled')}</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-green-600">
                {crawlStatus.pages_indexed}
              </div>
              <div className="text-sm text-gray-500">{t('onboarding.indexed')}</div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-500"
              style={{
                width: crawlStatus.pages_discovered > 0
                  ? `${(crawlStatus.pages_indexed / crawlStatus.pages_discovered) * 100}%`
                  : '0%'
              }}
            />
          </div>
        </div>
      )}

      {error && (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <XCircle className="w-5 h-5 inline mr-2" />
          {error}
          <button
            onClick={() => setStep('crawl')}
            className="block mt-3 text-sm underline hover:no-underline"
          >
            {t('onboarding.tryAgain')}
          </button>
        </div>
      )}
    </div>
  );

  const renderTest = () => (
    <div className="max-w-2xl mx-auto pb-8">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {t('onboarding.testTitle')}
        </h2>
        <p className="text-gray-600">
          {t('onboarding.testSubtitle')}
        </p>
      </div>

      {!testResults && (
        <div className="text-center">
          <button
            onClick={handleTestAI}
            disabled={isTestingAI}
            className="px-8 py-3 bg-black text-white rounded-full font-medium hover:bg-gray-800 disabled:bg-gray-300 transition flex items-center justify-center gap-2 mx-auto"
          >
            {isTestingAI ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {t('onboarding.testing')}
              </>
            ) : (
              <>
                <Bot className="w-5 h-5" />
                {t('onboarding.testAI')}
              </>
            )}
          </button>

          <button
            onClick={() => setStep('theme')}
            className="mt-4 text-gray-500 hover:text-gray-700"
          >
            {t('onboarding.skipTest')}
          </button>
        </div>
      )}

      {testResults && (
        <div className="space-y-4">
          {testResults.map((result, index) => (
            <div key={index} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-4 h-4 text-gray-600" />
                </div>
                <div className="font-medium text-gray-900">{result.question}</div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center flex-shrink-0">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div>
                  <div className="text-gray-700">{result.answer}</div>
                  <div className="mt-2 flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      result.confidence >= 70
                        ? 'bg-green-100 text-green-700'
                        : result.confidence >= 40
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {result.confidence}% {t('onboarding.confidence')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}

          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
            <button
              onClick={() => setStep('theme')}
              className="px-8 py-3 bg-black text-white rounded-full font-medium hover:bg-gray-800 transition flex items-center justify-center gap-2"
            >
              {t('onboarding.looksGood')}
              <ArrowRight className="w-5 h-5" />
            </button>
            <button
              onClick={handleTestAI}
              className="px-8 py-3 text-gray-600 hover:text-gray-900 transition"
            >
              {t('onboarding.testAgain')}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-center">
          {error}
        </div>
      )}
    </div>
  );

  const renderTheme = () => (
    <div className="max-w-xl mx-auto">
      <button
        onClick={() => setStep('test')}
        className="text-gray-500 hover:text-gray-700 mb-6 flex items-center gap-1"
      >
        &larr; {t('common.back')}
      </button>

      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Palette className="w-8 h-8 text-purple-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {t('onboarding.themeTitle')}
        </h2>
        <p className="text-gray-600">
          {t('onboarding.themeSubtitle')}
        </p>
        <p className="text-sm text-gray-500 mt-2">
          {t('onboarding.themeQuickHint')}
        </p>
      </div>

      {isExtractingBrand && (
        <div className="text-center mb-6">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-500">{t('onboarding.extractingBrand')}</p>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
        {/* Theme Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('onboarding.themeName')}
          </label>
          <input
            type="text"
            value={themeName}
            onChange={(e) => setThemeName(e.target.value)}
            placeholder={t('onboarding.themeNamePlaceholder')}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
          />
        </div>

        {/* Primary Color */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('onboarding.primaryColor')}
          </label>
          <div className="flex gap-3">
            <input
              type="color"
              value={themeConfig.appearance.colors.primary}
              onChange={(e) => setThemeConfig(prev => ({
                ...prev,
                appearance: {
                  ...prev.appearance,
                  colors: { ...prev.appearance.colors, primary: e.target.value }
                }
              }))}
              className="w-12 h-12 rounded-lg cursor-pointer border border-gray-300"
            />
            <input
              type="text"
              value={themeConfig.appearance.colors.primary}
              onChange={(e) => setThemeConfig(prev => ({
                ...prev,
                appearance: {
                  ...prev.appearance,
                  colors: { ...prev.appearance.colors, primary: e.target.value }
                }
              }))}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
            />
          </div>
        </div>

        {/* Preview */}
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <p className="text-sm text-gray-500 mb-3">{t('onboarding.themePreview')}</p>
          <div className="flex items-center justify-end">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all"
              style={{ backgroundColor: themeConfig.appearance.colors.primary }}
            >
              <MessageSquare className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        <button
          onClick={handleCreateTheme}
          disabled={isSubmitting || !themeName.trim()}
          className="w-full px-6 py-3 bg-black text-white rounded-full font-medium hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              {t('onboarding.creatingTheme')}
            </>
          ) : (
            <>
              {t('onboarding.createAndActivate')}
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>

        <button
          onClick={() => setStep('domains')}
          className="w-full text-gray-500 hover:text-gray-700 transition"
        >
          {t('onboarding.skipTheme')}
        </button>
      </div>
    </div>
  );

  const renderDomains = () => (
    <div className="max-w-xl mx-auto">
      <button
        onClick={() => setStep('theme')}
        className="text-gray-500 hover:text-gray-700 mb-6 flex items-center gap-1"
      >
        &larr; {t('common.back')}
      </button>

      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Globe className="w-8 h-8 text-orange-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {t('onboarding.domainsTitle')}
        </h2>
        <p className="text-gray-600">
          {t('onboarding.domainsSubtitle')}
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
        {/* Add Domain */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('onboarding.addDomain')}
          </label>
          <div className="flex gap-3">
            <input
              type="text"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder={t('onboarding.domainPlaceholder')}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
              onKeyDown={(e) => e.key === 'Enter' && handleAddDomain()}
            />
            <button
              onClick={handleAddDomain}
              disabled={isAddingDomain || !newDomain.trim()}
              className="px-4 py-3 bg-black text-white rounded-lg hover:bg-gray-800 disabled:bg-gray-300 transition flex items-center gap-2"
            >
              {isAddingDomain ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Plus className="w-5 h-5" />
              )}
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            {t('onboarding.domainHint')}
          </p>
        </div>

        {/* Domain List */}
        {domains.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('onboarding.allowedDomains')}
            </label>
            <div className="space-y-2">
              {domains.map(domain => (
                <div
                  key={domain.id}
                  className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="flex items-center gap-3">
                    <Check className="w-4 h-4 text-green-500" />
                    <span className="text-gray-900">{domain.host}</span>
                  </div>
                  <button
                    onClick={() => handleRemoveDomain(domain.id)}
                    className="p-1 text-gray-400 hover:text-red-500 transition"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {domains.length === 0 && (
          <div className="text-center py-6 text-gray-500">
            <Globe className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p>{t('onboarding.noDomainsYet')}</p>
          </div>
        )}

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        <button
          onClick={() => setStep('deploy')}
          disabled={domains.length === 0}
          className="w-full px-6 py-3 bg-black text-white rounded-full font-medium hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
        >
          {t('onboarding.continueToLaunch')}
          <ArrowRight className="w-5 h-5" />
        </button>

        <button
          onClick={() => setStep('deploy')}
          className="w-full text-gray-500 hover:text-gray-700 transition"
        >
          {t('onboarding.skipDomains')}
        </button>
      </div>
    </div>
  );

  const renderDeploy = () => (
    <div className="max-w-xl mx-auto text-center pb-8">
      <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
        <CheckCircle2 className="w-10 h-10 text-white" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">
        {t('onboarding.deployTitle')}
      </h2>
      <p className="text-gray-600 mb-8">
        {t('onboarding.deploySubtitle')}
      </p>

      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
        <div className="flex items-center gap-3 mb-4">
          <FileText className="w-5 h-5 text-gray-500" />
          <span className="font-medium text-gray-900">{t('onboarding.whatNext')}</span>
        </div>
        <ul className="text-left space-y-3 text-sm text-gray-600">
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
            {t('onboarding.next1')}
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
            {t('onboarding.next2')}
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
            {t('onboarding.next3')}
          </li>
        </ul>
      </div>

      {/* Customize further hint */}
      <div className="bg-purple-50 rounded-xl border border-purple-100 p-4 mb-4">
        <div className="flex items-start gap-3">
          <Palette className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-purple-900 font-medium">{t('onboarding.customizeMore')}</p>
            <p className="text-sm text-purple-700 mt-1">{t('onboarding.customizeMoreDesc')}</p>
          </div>
        </div>
      </div>

      {/* Orchestrations hint */}
      <div className="bg-indigo-50 rounded-xl border border-indigo-100 p-4 mb-8">
        <div className="flex items-start gap-3">
          <Wand2 className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-indigo-900 font-medium">{t('onboarding.orchestrationsHint')}</p>
            <p className="text-sm text-indigo-700 mt-1">{t('onboarding.orchestrationsHintDesc')}</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      <button
        onClick={handleActivateAndFinish}
        disabled={isSubmitting}
        className="px-10 py-4 bg-black text-white rounded-full font-medium hover:bg-gray-800 disabled:bg-gray-300 transition flex items-center justify-center gap-2 mx-auto text-lg"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            {t('onboarding.activating')}
          </>
        ) : (
          <>
            <Sparkles className="w-5 h-5" />
            {t('onboarding.launchAI')}
          </>
        )}
      </button>
    </div>
  );

  const steps: Step[] = ['welcome', 'crawl', 'crawling', 'test', 'theme', 'domains', 'deploy'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-start justify-center px-6 py-12 overflow-y-auto">
      <div className="w-full max-w-4xl pb-12">
        {/* Progress indicator */}
        <div className="flex items-center justify-center gap-2 mb-12">
          {steps.map((s, i) => {
            const isActive = s === step;
            const isPast = steps.indexOf(step) > i;
            return (
              <div
                key={s}
                className={`w-3 h-3 rounded-full transition-all ${
                  isActive
                    ? 'w-8 bg-black'
                    : isPast
                    ? 'bg-green-500'
                    : 'bg-gray-300'
                }`}
              />
            );
          })}
        </div>

        {step === 'welcome' && renderWelcome()}
        {step === 'crawl' && renderCrawl()}
        {step === 'crawling' && renderCrawling()}
        {step === 'test' && renderTest()}
        {step === 'theme' && renderTheme()}
        {step === 'domains' && renderDomains()}
        {step === 'deploy' && renderDeploy()}
      </div>
    </div>
  );
}
